import { useMemo } from "react";

import type { MeetingModelJson } from "@performyard/service-contracts";

import { useInternalApiUser } from "../../../../api/hooks";

type UseManagerDirectReportMeetingParams = {
	meeting: MeetingModelJson | null | undefined;
	userId: string;
	isAdmin: boolean;
	isParticipant: boolean;
};

export const useManagerDirectReportMeeting = ({
	meeting,
	userId,
	isAdmin,
	isParticipant,
}: UseManagerDirectReportMeetingParams): boolean => {
	const isAdminNonParticipant = isAdmin && !isParticipant;
	const meetingParticipantIds = useMemo(
		() => meeting?.participants?.map((participant) => participant.userId).filter(Boolean) || [],
		[meeting?.participants]
	);
	const isOneToOneMeeting = meetingParticipantIds.length === 2;
	const otherParticipantId = meetingParticipantIds.find((participantId) => participantId !== userId);

	const shouldFetch = isOneToOneMeeting && !isAdminNonParticipant && Boolean(userId);

	const { data: currentUserInfo } = useInternalApiUser(userId, {
		enabled: shouldFetch,
	});
	const { data: otherParticipantInfo } = useInternalApiUser(otherParticipantId || "", {
		enabled: shouldFetch && Boolean(otherParticipantId),
	});

	return useMemo(() => {
		if (isAdminNonParticipant || !isOneToOneMeeting || !otherParticipantId) {
			return false;
		}

		// The meeting is a manager-direct report 1:1 if the org-chart manager relationship
		// exists in either direction between the two participants. We check both sides to
		// ensure both the manager AND the direct report see manager-DR features.
		const currentUserDirectReports = currentUserInfo?.directReports || [];
		const otherParticipantDirectReports = otherParticipantInfo?.directReports || [];

		return (
			currentUserDirectReports.some((report) => report._id === otherParticipantId) ||
			otherParticipantDirectReports.some((report) => report._id === userId)
		);
	}, [
		currentUserInfo?.directReports,
		otherParticipantInfo?.directReports,
		isAdminNonParticipant,
		isOneToOneMeeting,
		otherParticipantId,
		userId,
	]);
};
